import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Lightning, TextT, X } from "@phosphor-icons/react";
import { defaultSkill, skillSchema } from "@stylingcv/schema";
import {
  Badge,
  BadgeInput,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Slider,
} from "@stylingcv/ui";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { SectionDialog } from "../sections/shared/section-dialog";

const COMMON_SKILLS = [
  "JavaScript", "TypeScript", "Python", "Java", "C++", "C#", "Go", "Rust", "Ruby", "PHP", "Swift",
  "Kotlin", "SQL", "HTML", "CSS", "Sass", "React", "Next.js", "Vue.js", "Angular", "Svelte",
  "Node.js", "Express.js", "NestJS", "Django", "Flask", "Spring Boot", "Laravel", "Ruby on Rails",
  "ASP.NET", "FastAPI", "GraphQL", "REST API", "gRPC", "WebSocket",
  "PostgreSQL", "MySQL", "MongoDB", "Redis", "DynamoDB", "Elasticsearch", "SQLite", "Firebase",
  "AWS", "Azure", "Google Cloud", "Docker", "Kubernetes", "Terraform", "CI/CD", "GitHub Actions",
  "Jenkins", "GitLab CI", "Linux", "Nginx", "Apache",
  "Git", "Jira", "Confluence", "Figma", "Sketch", "Adobe XD", "Photoshop", "Illustrator",
  "TailwindCSS", "Bootstrap", "Material UI", "Chakra UI",
  "Jest", "Vitest", "Cypress", "Playwright", "Selenium", "Testing Library",
  "Machine Learning", "Deep Learning", "TensorFlow", "PyTorch", "NLP", "Computer Vision",
  "Data Analysis", "Pandas", "NumPy", "Scikit-learn", "Tableau", "Power BI",
  "Agile", "Scrum", "Kanban", "Project Management", "Team Leadership", "Communication",
  "Problem Solving", "Critical Thinking", "Time Management", "Mentoring",
  "SEO", "Google Analytics", "Content Marketing", "Social Media Marketing",
  "Salesforce", "HubSpot", "SAP", "Excel", "Word", "PowerPoint",
  "Blockchain", "Web3", "Solidity", "Smart Contracts",
  "UI/UX Design", "Responsive Design", "Accessibility", "Performance Optimization",
  "Microservices", "System Design", "Design Patterns", "Clean Architecture",
  "OAuth", "JWT", "Security", "Penetration Testing",
  "Mobile Development", "React Native", "Flutter", "iOS", "Android",
];

const formSchema = skillSchema;

type FormValues = z.infer<typeof formSchema>;

export const SkillsDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: defaultSkill,
    resolver: zodResolver(formSchema),
  });

  const [pendingKeyword, setPendingKeyword] = useState("");

  const currentKeywords = form.watch("keywords");
  const filteredSuggestions = useMemo(
    () => COMMON_SKILLS.filter((s) => !currentKeywords.includes(s)),
    [currentKeywords],
  );

  return (
    <SectionDialog<FormValues>
      id="skills"
      form={form}
      defaultValues={defaultSkill}
      pendingKeyword={pendingKeyword}
    >
      <div className="grid grid-cols-1 gap-4 p-2 sm:grid-cols-2">
        <FormField
          name="name"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Name`}</FormLabel>
              <FormControl>
                <Input {...field} leadingIcon={<Lightning size={15} />} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="description"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Description`}</FormLabel>
              <FormControl>
                <Input {...field} leadingIcon={<TextT size={15} />} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="level"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t`Level`}</FormLabel>
              <FormControl className="py-2">
                <div className="flex items-center gap-x-4">
                  <Slider
                    {...field}
                    min={0}
                    max={5}
                    value={[field.value]}
                    orientation="horizontal"
                    onValueChange={(value) => {
                      field.onChange(value[0]);
                    }}
                  />

                  {field.value > 0 ? (
                    <span className="text-base font-bold">{field.value}</span>
                  ) : (
                    <span className="text-base font-bold">{t`Hidden`}</span>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="keywords"
          control={form.control}
          render={({ field }) => (
            <div className="space-y-3 sm:col-span-2">
              <FormItem>
                <FormLabel>{t`Keywords`}</FormLabel>
                <FormControl>
                  <BadgeInput
                    {...field}
                    setPendingKeyword={setPendingKeyword}
                    suggestions={filteredSuggestions}
                  />
                </FormControl>
                <FormDescription>
                  {t`You can add multiple keywords by separating them with a comma or pressing enter.`}
                </FormDescription>
                <FormMessage />
              </FormItem>

              <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
                <AnimatePresence>
                  {field.value.map((item, index) => (
                    <motion.div
                      key={item}
                      layout
                      initial={{ opacity: 0, y: -50 }}
                      animate={{ opacity: 1, y: 0, transition: { delay: index * 0.1 } }}
                      exit={{ opacity: 0, x: -50 }}
                    >
                      <Badge
                        className="cursor-pointer"
                        onClick={() => {
                          field.onChange(field.value.filter((v) => item !== v));
                        }}
                      >
                        <span className="me-1">{item}</span>
                        <X size={12} weight="bold" />
                      </Badge>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}
        />
      </div>
    </SectionDialog>
  );
};
